 
import { Box, Stack, Typography, useTheme } from '@mui/joy';
import DonorLegend from './DonorLegend';
import { useDonorSectionData } from '../../providers/DonorSectionProvider';
import UiTooltip from '../Controls/UiTooltip';
import ChartTooltip from './ChartTooltip';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useRoute } from '../../providers/RouteProvider';

const DotPlots = ({
	i,
	topic,
	page,
	print
}: {
	i: number;
	topic: string;
	page: string;
	print?: boolean;
}) => {
	const theme = useTheme();
	const { election } = useRoute();
	const TICKS = [0, 20, 40, 60, 80, 100];
	const matches = useMediaQuery(theme.breakpoints.up('md'));
	const matchesSM = useMediaQuery(theme.breakpoints.up('sm'));
	const isDonorState = page === 'donorState';
	const { chartData } = useDonorSectionData();
	const sorted = chartData?.map((array) => {
		const sortedByLabel = array.map((arr) => {
			const order = [
				'Democratic Men',
				'Democratic Women',
				'Republican Women',
				'Republican Men'
			];
			return order
				.map((label) => {
					return arr.find((d) => d.label === label);
				})
				.filter((item) => item);
		}) as { [i: string]: string | number }[][];

		return array.length
			? sortedByLabel.map((item) =>
					item.sort((a, b) => (+a.value > +b.value ? 1 : -1))
			  )
			: null;
	});
	const names: { [x: string]: { [x: number]: string } } = {
		state: {
			0: 'State Senate',
			1: 'State House',
			2: 'Statewide Executive Offices'
		},
		congress: { 0: 'US Senate', 1: 'US House' }
	};
	const calcHeights = (array: { [i: string]: string | number }[]) => {
		const top = !matchesSM ? 3.365 : 3.175;
		let rePosFirst = false,
			rePosSecond = false,
			rePosThird = false;
		const isAllTheSame = (array: { [i: string]: string | number }[]) => {
			return array.every(({ value }) => value === array[0].value);
		};
		const same = array.length === 4 ? isAllTheSame(array) : false;
		const valLookUp: { [i: number]: number } = {
			0: top - 0.9,
			1: top - 0.3,
			2: top + 0.3,
			3: top + 0.9
		};
		const ss = array.map((v, index) => {
			//if all value are the same --> spread all four values farther apart.
			if (same) {
				v.top = valLookUp[index];
			}
			//if first in array is same as next in array, make it higher
			else if (
				array[index] &&
				array[index + 1] &&
				index === 0 &&
				+(+array[index + 1].value === +array[index].value)
			) {
				v.top = top - 0.6;
				rePosFirst = true;
			} else if (array[index] && index === 1) {
				if (
					rePosFirst &&
					array[index + 1] &&
					+(+array[index + 1].value === +array[index].value)
				) {
					v.top = top; //put 2nd one in middle if 1st and 3rd are on same point
					rePosSecond = true;
				} else if (rePosFirst) {
					v.top = top + 0.6;
					rePosSecond = true;
				} else if (
					array[index + 1] &&
					+(+array[index + 1].value === +array[index].value)
				) {
					v.top = top - 0.6;
					rePosSecond = true;
				} // } else if (
				// 	array[index + 1] &&
				// 	+(+array[index + 1].value === +array[index].value)
				// ) {
				// 	v.top = top + 0.6;
				// 	rePosSecond = true;
				// } else v.top = top;
			} else if (array[index] && index === 2) {
				if (
					rePosFirst &&
					rePosSecond &&
					+(+array[index].value === +array[index - 1].value) // 1,2,3 on same spot
				) {
					v.top = top + 0.6;
					rePosThird = true;
				} else if (
					+(+array[index].value === +array[index - 1].value) &&
					array[index + 1] &&
					+(+array[index].value === +array[index + 1].value) // 2,3,4 are on same spot
				) {
					v.top = top;
					rePosThird = true;
				} else if (
					rePosSecond &&
					+(+array[index].value === +array[index - 1].value) // 2,3 on same spot
				) {
					v.top = top + 0.6;
					rePosThird = true;
				} else if (
					array[index + 1] &&
					+(+array[index + 1].value === +array[index].value) //3 and 4 are on same spot
				) {
					v.top = top - 0.6;
					rePosThird = true;
				} else {
					v.top = top;
				}
			} else if (array[index] && index === 3) {
				if (
					//rePosSecond &&
					rePosThird &&
					+(+array[index].value === +array[index - 1].value) &&
					+(+array[index - 2].value !== +array[index - 1].value) //and val of 2 is diff from 3, i.e. not a threesome
				) {
					v.top = top + 0.6;
				} else if (
					rePosSecond &&
					rePosThird &&
					+(+array[index].value === +array[index - 1].value)
				) {
					v.top = top + 0.6; // 2,3,4 are on same spot
					// } else if (
					// 	rePosThird &&
					// 	+(+array[index].value === +array[index - 1].value)
					// ) {
					// 	v.top = top + 0.6;
				} else v.top = top;
			}

			return v;
		});
		return ss;
	};
	return (
		<>
			<Box sx={{ position: 'relative', mt: '1.5rem' }}>
				<Box
					key={`${i}box`}
					sx={{
						position: 'absolute',
						top: 0,
						width: '100%',
						height: 'calc(100% - 3rem)'
					}}
				>
					{TICKS.map((tick, i) => (
						<>
							<Box
								key={tick}
								sx={{
									borderRight:
										i === 0 ? '1px solid black' : '1px solid lightGray',
									height: '100%',
									position: 'absolute',
									top: 0,
									left: `${tick}%}`
								}}
							/>
						</>
					))}
				</Box>
				<>
					{sorted && sorted.length
						? sorted[i]?.map((array, j) => {
								//console.log('array', array);
								calcHeights(array);
								return (
									<Box
										key={`${i}${j}box`}
										sx={{
											width: '100%',
											height: '8rem',
											backgroundColor:
												i % 2 === 0
													? 'rgba(215, 221, 223, 0.6)'
													: 'rgba(215, 221, 223, 0.3)',
											marginBottom: '1rem',
											position: 'relative'
										}}
									>
										<Typography
											level={'label'}
											sx={{
												padding: '.5rem',
												color:
													theme.colorSchemes.light.palette
														.chartElementsBackground.xAxisLabels
											}}
										>
											{isDonorState
												? names[election as string][j]
												: names['congress' as string][j]}
											{/* {array[0].officeLevel === 'STW'
									 ? 'Statewide Executive Offices'
									 : array[0].chamber} */}
										</Typography>

										{array?.map((d, i) => {
											//console.log('value array', array);
											return d && array.length <= 4 ? (
												<UiTooltip
													key={`${i}tooltip`}
													title={
														<ChartTooltip
															titleText={
																isDonorState
																	? names[election as string][j]
																	: names['congress' as string][j]
															}
															d={
																d as {
																	[x: string]: string | number;
																}
															}
															topic={topic}
															donor={true}
														/>
													}
													variant='plain'
													sx={{ borderRadius: '0px' }}
													arrow
													placement={!matchesSM ? 'top' : 'left'}
												>
													<Box
														key={`${i}dot`}
														sx={{
															width: !matchesSM ? '1.25rem' : '1.75rem',
															height: !matchesSM ? '1.25rem' : '1.75rem',
															borderRadius: '50%',
															backgroundColor: d.color,
															position: 'absolute',
															left: `calc(${(+d.value * 100).toFixed()}% - ${
																!matchesSM ? 1.25 / 2 : 1.75 / 2
															}rem)`,
															top: d.top
																? `${d.top}rem`
																: !matchesSM
																? '3.365rem'
																: '3.175rem',
															zIndex: 3,
															display: isNaN(+d.value) ? 'none' : 'block',
															transition: 'all 1s ease'
														}}
													/>
												</UiTooltip>
											) : null;
										})}
										{array[0] && array.length ? (
											<>
												<Box
													sx={{
														position: 'absolute',
														left: `calc(${+array[0].value * 100}%)`,
														top: '4rem',
														width: `calc(${
															(+array[array.length - 1].value -
																+array[0].value) *
															100
														}%)`,
														borderTop: '2px solid black',
														transition: 'all 1s ease'
													}}
												></Box>
												<Box
													sx={{
														position: 'absolute',
														left: `calc(${
															+array[array.length - 1].value * 100
														}% + 1.4rem)`,
														top: '3rem',
														color: array[array.length - 1].color,
														fontFamily: 'Source Sans Pro',
														fontSize: '21px',
														fontWeight: 700
													}}
												>
													{`${(
														+array[array.length - 1].value * 100
													).toFixed()}%`}
												</Box>
											</>
										) : null}
									</Box>
								);
						  })
						: null}
				</>
				<Stack
					direction='row'
					justifyContent='space-between'
					sx={{
						marginLeft: '-.25rem',
						marginRight: '-2rem',
						fontFamily: 'Source Sans Pro',
						fontSize: '20px'
					}}
				>
					<Box>{'0%'}</Box>
					<Box>{'100%'}</Box>
				</Stack>
			</Box>

			{!matches && i === 0 ? <DonorLegend print={print} /> : null}
		</>
	);
};

export default DotPlots;
